import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { LabConfirmationCoverageData } from '../LabConfirmationCoverage';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const useLabConfirmationCoverage = (
    investigation: { uuid: string } | undefined,
    startDate: number | null,
    endDate: number | null,
) => {
    const [data, setData] = useState<LabConfirmationCoverageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            if (!investigation?.uuid) return;
            const end = endDate ?? Date.now();
            const start = startDate ?? end - THIRTY_DAYS_MS;
            setLoading(true);
            setError(null);
            try {
                const result = await clinicalServices.getLabConfirmationCoverage(
                    investigation.uuid,
                    start,
                    end,
                    source.token,
                );
                if (superseded()) return;
                setData(result);
            } catch (err) {
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, startDate, endDate, reloadToken]);

    return { data, loading, error, retry };
};
